import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1534953342533-7711c98712be?auto=format&fit=crop&w=900&q=72";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "buyer" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await register(form);
      nav(u.role === "seller" ? "/sell" : "/account");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 bg-sand order-2 lg:order-1">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-mute hover:text-clay mb-8"><ArrowLeft className="w-4 h-4" /> Back home</Link>
          <div className="overline text-clay mb-3">Create account</div>
          <h1 className="font-serif text-4xl tracking-tight mb-6">Join the makers<br /> & the gifted.</h1>
          {err && <div className="bg-destructive/10 text-destructive text-sm rounded-sm px-4 py-3 mb-4" data-testid="register-error">{err}</div>}

          <div className="flex gap-2 mb-5">
            {[["buyer", "I want to gift"], ["seller", "I want to sell"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setForm({ ...form, role: v })}
                className={`flex-1 rounded-full py-2.5 text-sm border transition-colors ${form.role === v ? "bg-clay text-sand border-clay" : "border-line text-ink/70"}`}
                data-testid={`role-${v}`}>{l}</button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="overline text-mute block mb-1.5">Full name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="register-name" />
            </div>
            <div>
              <label className="overline text-mute block mb-1.5">Email</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="register-email" />
            </div>
            <div>
              <label className="overline text-mute block mb-1.5">Password</label>
              <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="register-password" />
            </div>
            <button type="submit" disabled={loading} className="w-full rounded-full bg-clay text-sand py-3.5 text-sm hover:bg-forest transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="register-submit">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : "Create account"}
            </button>
          </form>
          <p className="text-sm text-mute mt-6">Already have an account? <Link to="/login" className="text-clay link-underline">Sign in</Link></p>
        </motion.div>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-clay/30" />
        <div className="absolute bottom-10 left-10 right-10 text-sand">
          <p className="font-serif text-4xl leading-tight tracking-tight">Every gift here<br /> is made by hand.</p>
        </div>
      </div>
    </div>
  );
}
